import jwtdecode from "jwt-decode";
import { get, isEmpty } from "lodash";

import { getInstance } from "@/auth";
import ROLES_AND_PERMISSIONS from "@/constants/roles-and-permissions";
import { FEATURES, FEATURE_ACCESS_VISIBILITY } from "@/constants/system-feature";
import GetSimpatraServiceProxy from "@/proxies/back-end/simpatra-service/get";
import PostSimpatraServiceProxy from "@/proxies/back-end/simpatra-service/post";
import PutSimpatraServiceProxy from "@/proxies/back-end/simpatra-service/put";
import DeleteSimpatraServiceProxy from "@/proxies/back-end/simpatra-service/delete";

const executePostQuery = async (query, payload, notificationParams) => {
  const proxy = new PostSimpatraServiceProxy();
  const response = await proxy.execute(query, payload, notificationParams);
  return response ? response.data : null;
};

const executeGetQuery = async (query, queryParams = {}) => {
  const proxy = new GetSimpatraServiceProxy();
  const response = await proxy.execute(query, queryParams);
  return response ? response.data : null;
};

const executePutQuery = async (query, payload, notificationParams) => {
  const proxy = new PutSimpatraServiceProxy();
  const response = await proxy.execute(query, payload, notificationParams);
  return response ? response.data : null;
};

const buildOrgUserLicensesQuery = orgUserId =>
  `accounts/v1/api/${orgUserId}/user-license`;

// OPTIMIZE
const executeQuery = async (
  method,
  query,
  payload = {},
  notificationParams,
) => {
  let proxy;
  switch (method) {
    case "get":
      proxy = new GetSimpatraServiceProxy();
      return (await proxy.execute(query, payload))?.data || null;
    case "post":
      proxy = new PostSimpatraServiceProxy();
      return (
        (await proxy.execute(query, payload, notificationParams))?.data || null
      );
    case "put":
      proxy = new PutSimpatraServiceProxy();
      return (
        (await proxy.execute(query, payload, notificationParams))?.data || null
      );
    case "delete":
      proxy = new DeleteSimpatraServiceProxy();
      return (
        (await proxy.execute(query, { notificationParams, payload }))?.data || null
      );
    default:
      throw new Error(`Unsupported method: ${method}`);
  }
};

/**
 * USING DECODED DATA IN THE TOKEN
 * This is for security purpose
 */
const initializeCurrentLoggedInInfo = ({ commit }, payload) => {
  commit("setCurrentLoggedInInfo", payload);
};

const fetchLoggedUserAccessibleLocations = async (_, queryParams = {}) => {
  const proxy = new GetSimpatraServiceProxy();
  // orgId query only works for super admin whenever user is trying to access other's org
  const query = "accounts/v1/user/location-access";
  const response = await proxy.execute(query, queryParams);
  if (response) {
    const { data } = response;
    return data;
  }
};

const initializeLoginDetails = (_, loginDetails) => {
  // eslint-disable-next-line camelcase
  const { access_token } = loginDetails;
  const decodedToken = jwtdecode(access_token);
  const { id } = decodedToken.sim.details; // orgId or vendorId
  localStorage.setItem("access_token", access_token);
  localStorage.setItem("exp", decodedToken.exp);
  localStorage.removeItem("signatureImage");
  localStorage.setItem("orgId", id);
  return decodedToken;
};

const fetchMyUserInfo = async ({ commit }) => {
  const proxy = new GetSimpatraServiceProxy();
  const accessToken = localStorage.getItem("access_token");
  const decodedToken = jwtdecode(accessToken);
  const { id } = decodedToken.sim.details; // orgId or vendorId
  const userId = decodedToken.sim.user.subject; // orgId or vendorId
  const query = `accounts/v1/org/${id}/user/${userId}`;
  const response = await proxy.execute(query);
  if (response) {
    const { data } = response;
    commit("setCurrentUserInfo", data || null);
    return data;
  }
};

const getSpecificRole = ({ state }, payload) => {
  const { section } = payload;
  return (
    state.userRolesAndPermission?.find(
      userRole => userRole.section.name === section,
    ) || null
  );
};

// @ Returns either user has "read", "editable", or unauthorized access
// Payload accepts [FEATURES.API_ACCESS] enums
const getFeatureAccess = async ({ getters }, payload) => {
  const featureAccess = getters.getFeatureAccess;
  const { feature } = payload;
  const selectedFeature = await featureAccess.find(
    item => item.feature === feature,
  );
  const { FULL, EDIT, READ, UNAUTHORIZED } =
    ROLES_AND_PERMISSIONS.ROLE_ACCESS_TYPE;
  const { EDITABLE, READ_ONLY } = ROLES_AND_PERMISSIONS.ACCESS_LEVELS;
  if (!selectedFeature || selectedFeature.accessType === UNAUTHORIZED) {
    return null;
  }
  if ([FULL, EDIT, EDITABLE].includes(selectedFeature.accessType)) {
    return EDITABLE;
  }
  if (selectedFeature.accessType === READ) {
    return READ;
  }
  return READ_ONLY;
};

// Helper function to check role permission access
function hasFeatureAccess(userRole, sectionPermissions, enableDebug = false) {
  if (!sectionPermissions || isEmpty(sectionPermissions.permissions)) {
    return false;
  }

  return sectionPermissions.permissions.some(requiredPermission => {
    const hasRequiredPermission = userRole.name.includes(
      requiredPermission.permission,
    );
    const hasCorrectAccessLevel =
      userRole.selectionType === requiredPermission.accessLevel;

    if (enableDebug) {
      console.log({
        roleName: userRole.name,
        sectionName: userRole.section.name,
        hasRequiredPermission,
        hasCorrectAccessLevel,
        requiredPermission,
        userSelectionType: userRole.selectionType,
      });
    }

    return hasRequiredPermission && hasCorrectAccessLevel;
  });
}

// Returns Boolean if user has access to this page (feature)
const getPageAccess = async ({ state, getters }, { feature } = {}) => {
  const { isSimpatraAdmin } = state.currentUserPermissions;

  if (isSimpatraAdmin) return true;

  const featurePermissionsSections = FEATURE_ACCESS_VISIBILITY[feature];
  if (isEmpty(featurePermissionsSections)) return true;

  const userRoles = state.userRolesAndPermission || [];
  const enableDebug = false; // set to true if you want to see logs!

  return userRoles.some(userRole => {
    const sectionPermissions =
      featurePermissionsSections[userRole.section.name];
    return hasFeatureAccess(userRole, sectionPermissions, enableDebug);
  });
};

const getSubFeatureAccess = async ({ getters }, payload) => {
  const featureAccess = getters.getFeatureAccess;
  const { feature, subFeature } = payload;

  const selectedFeature = await featureAccess.find(
    item => item.feature === feature,
  );
  if (!selectedFeature || !selectedFeature.subFeature) return null;

  const selectedSubFeature = selectedFeature.subFeature.find(
    item => item.name === subFeature,
  );
  const { FULL, EDIT, READ, UNAUTHORIZED } =
    ROLES_AND_PERMISSIONS.ROLE_ACCESS_TYPE;
  const { EDITABLE, READ_ONLY } = ROLES_AND_PERMISSIONS.ACCESS_LEVELS;
  if (!selectedSubFeature || selectedSubFeature.accessType === UNAUTHORIZED) {
    return null;
  }
  if ([FULL, EDIT, EDITABLE].includes(selectedSubFeature.accessType)) {
    return EDITABLE;
  }
  if (selectedSubFeature.accessType === READ) {
    return READ;
  }
  return READ_ONLY;
};

// Checking if current logged in is an internal user
const getAuthUserRoles = async () => {
  const authService = await getInstance();
  const internalUserInfo = await authService.user;
  return (
    internalUserInfo?.["https://providerportal.biote.com/roles"] || []
  );
};

const hasRole = (roles, targetRole) => roles?.includes(targetRole);

const hasAnyRole = (roles, targetRoles) =>
  targetRoles.some(targetRole => hasRole(roles, targetRole));

const PROVIDER_AUTH0_ROLES = ["Provider", "ProviderFinancials"];

const hasProviderRole = async () => {
  const userRoles = await getAuthUserRoles();
  return hasAnyRole(userRoles, PROVIDER_AUTH0_ROLES);
};

const hasBioteSalesRole = async () => {
  const userRoles = await getAuthUserRoles();
  return hasRole(userRoles, "BioteSales");
};

const isBioteSalesUser = async () => {
  return hasBioteSalesRole();
};

const isInternalUser = async () => {
  const userRoles = await getAuthUserRoles();
  return hasRole(userRoles, "BioteCustomerService");
};

const isNonPrescriberUser = async () => {
  const userRoles = await getAuthUserRoles();
  const isProvider = hasAnyRole(userRoles, PROVIDER_AUTH0_ROLES);
  const isBioteSales = hasRole(userRoles, "BioteSales");
  return !isProvider && !isBioteSales;
};

// Returns Boolean if user is a super admin
const isSuperAdminUser = ({ state }) => {
  const { isSimpatraAdmin } = state.currentUserPermissions;
  return isSimpatraAdmin;
};

const canManageBundles = async ({ dispatch, getters, state }) => {
  const sim = getters.getMyInfo?.sim;
  const isSimpatraAdmin =
    Boolean(get(state, "currentUserPermissions.isSimpatraAdmin")) ||
    Boolean(get(sim, "simpatraAdmin")) ||
    Boolean(get(sim, "user.simpatraAdmin"));
  if (isSimpatraAdmin) return true;
  return Boolean(await dispatch("getPageAccess", { feature: FEATURES.VENDOR_MANAGEMENT }));
};

const denyUserRole = async () => {
  const query = "user-access-control/v1/user/prescriber/deny";
  const notificationParams = {
    displaySuccessMessage: true,
    successNotificationMsg: "Successfully denied access!",
    successNotificationHeader: "Successfully Denied!",
  };
  return await executePostQuery(query, {}, notificationParams);
};

const fetchUserLicense = async ({ commit }, { state, orgId, queryParams = {} }) => {
  const query = `accounts/v1/licenses/state/${state}`;
  const data = await executeQuery("get", query, queryParams);
  commit("setUserLicenses", data);
  return data;
};

const addUserLicense = async (_, payload) => {
  // Check if orgUserId is an object and get the key if it is
  if (typeof payload.orgUserId === "object" && payload.orgUserId !== null) {
    payload.orgUserId = payload.orgUserId.key; // Get the key from the object
  }

  const notificationParams = {
    displaySuccessMessage: true,
    successNotificationMsg: "Successfully added license!",
    successNotificationHeader: "Successfully Added!",
  };
  return executeQuery(
    "post",
    "accounts/v1/user-license/create",
    payload,
    notificationParams,
  );
};

const editUserLicense = async (_, payload) => {
  const notificationParams = {
    displaySuccessMessage: true,
    successNotificationMsg: "Successfully edited license!",
    successNotificationHeader: "Successfully Edited!",
  };
  return executeQuery(
    "put",
    "/accounts/v1/user-license/update",
    payload,
    notificationParams,
  );
};

const deleteUserLicense = async (_, payload) => {
  const notificationParams = {
    displaySuccessMessage: true,
    successNotificationMsg: "Successfully deleted license!",
    successNotificationHeader: "Successfully Deleted!"
  };
  return executeQuery(
    "delete",
    "accounts/v1/user-license",
    payload,
    notificationParams,
  );
};

const fetchOrgUserLicenses = async (
  _,
  {
    orgUserId,
    state,
    params = {},
  } = {},
) => {
  if (!orgUserId) {
    throw new Error("User ID is required to fetch Org User Licenses.");
  }
  const query = buildOrgUserLicensesQuery(orgUserId);
  const queryParams = { ...params };
  if (state) {
    queryParams.state = state;
  }
  const data = await executeGetQuery(query, queryParams);
  return data;
};

const createOrgUserLicense = async (_, payload) => {
  const notificationParams = {
    displaySuccessMessage: true,
    successNotificationMsg: "Successfully added license!",
    successNotificationHeader: "Successfully Added!",
  };
  const query = "accounts/v1/api/user-license/create";
  return await executePostQuery(query, payload, notificationParams);
};

const updateOrgUserLicense = async (_, payload) => {
  const notificationParams = {
    displaySuccessMessage: true,
    successNotificationMsg: "Successfully updated license!",
    successNotificationHeader: "Successfully Updated!",
  };
  const query = "accounts/v1/api/user-license";
  return await executePutQuery(query, payload, notificationParams);
};

const deleteOrgUserLicense = async (_, payload) => {
  const notificationParams = {
    displaySuccessMessage: true,
    successNotificationMsg: "Successfully deleted license!",
    successNotificationHeader: "Successfully Deleted!",
  };
  const query = "accounts/v1/api/user-license/delete";
  return await executeQuery("delete", query, payload, notificationParams);
};

export default {
  initializeCurrentLoggedInInfo,
  fetchLoggedUserAccessibleLocations,
  initializeLoginDetails,
  fetchMyUserInfo,
  getSpecificRole,
  getFeatureAccess,
  getSubFeatureAccess,
  isInternalUser,
  isNonPrescriberUser,
  denyUserRole,
  getPageAccess,
  canManageBundles,
  fetchUserLicense,
  fetchOrgUserLicenses,
  addUserLicense,
  editUserLicense,
  deleteUserLicense,
  createOrgUserLicense,
  updateOrgUserLicense,
  deleteOrgUserLicense,
  isSuperAdminUser,
  hasProviderRole,
  hasBioteSalesRole,
  isBioteSalesUser,
};
